import {
  ButtonToggle,
  Flex,
  Skeleton,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import type { ShopperReservationMode } from '@iheartjane/types';
import { useState } from 'react';

export interface ReservationModeButton {
  closed?: boolean;
  label: string;
  mode: ShopperReservationMode;
  selected: boolean;
}

export interface ReservationModeToggleProps {
  buttonClass?: string;
  isLoading?: boolean;
  modes: ReservationModeButton[];
  onChange: (mode: ShopperReservationMode) => void;
}

export const ReservationModeToggle = ({
  buttonClass,
  isLoading = false,
  modes,
  onChange,
}: ReservationModeToggleProps) => {
  const [hasToggled, setHasToggled] = useState(false);
  const selectedMode = modes.find((mode) => mode.selected) ?? modes[0];

  return (
    <Flex width="100%" justifyContent="center" mt={16}>
      {isLoading && (
        <Flex width="100%" gap={16}>
          {Array.from({ length: modes.length }).map((_, index) => (
            <Skeleton.Bone
              height="48px"
              width={`calc((100% - ${16 * (modes.length - 1)}px) / ${modes.length})`}
              borderRadius="lg"
              key={index}
            />
          ))}
        </Flex>
      )}

      {!isLoading && modes.length > 1 && (
        <ButtonToggle
          value={selectedMode.mode}
          variant="tertiary"
          onChange={(mode) => {
            setHasToggled(true);
            onChange(mode);
          }}
        >
          {modes.map((mode) => (
            <ButtonToggle.Button
              // Restores focus after the loading skeleton remounts the buttons
              autoFocus={hasToggled && mode.mode === selectedMode.mode}
              className={buttonClass}
              key={mode.mode}
              value={mode.mode}
              disabled={mode.closed}
            >
              <Flex justifyContent="center">
                <Typography
                  color="inherit"
                  as="span"
                  variant="body-semibold"
                  maxLines={2}
                  overflowWrap="break-word"
                >
                  {mode.label}
                </Typography>
                {mode.closed && (
                  <Tag label="Closed" background="grays-black" ml={4} />
                )}
              </Flex>
            </ButtonToggle.Button>
          ))}
        </ButtonToggle>
      )}

      {!isLoading && modes.length === 1 && (
        <Typography variant="body-semibold" textAlign="center">
          {modes[0].label}
        </Typography>
      )}
    </Flex>
  );
};
